/**
 * @file BootShell.ts
 * @role Owns the static pre-JS loading shell (#boot-shell in index.html). The shell paints
 *       on first HTML parse and must stay visible until the Pixi splash scene is actually
 *       on screen, so slow networks never see a blank canvas between the two.
 * @owns Hiding the shell (Bootstrapper calls this after the splash is shown).
 * @reads document only. No Pixi, no game state.
 * @neverDoes Render game content. Decide boot flow (Bootstrapper owns ordering).
 * @invariants 21 (boot reaches a terminal state; the shell never lingers over a live splash).
 * @see index.html (#boot-shell), src/app/Bootstrapper.ts
 */

/** Hide the static boot shell. Idempotent; safe no-op if the element is absent (tests). */
export function hideBootShell(): void {
  if (typeof document === 'undefined') return;
  const shell = document.getElementById('boot-shell');
  if (!shell) return;
  shell.setAttribute('aria-hidden', 'true');
  shell.classList.add('boot-shell--hidden');
}

/**
 * Put the boot shell into a terminal error state (Invariant 21: boot never hangs silently).
 * Re-shows the shell if it was already hidden, so this also works as the last-resort surface
 * for failures after the splash took over. Repeated calls replace the message and handler.
 */
export function showBootShellError(message: string, onRetry: () => void): void {
  if (typeof document === 'undefined') return;
  const shell = document.getElementById('boot-shell');
  if (!shell) return;
  shell.classList.remove('boot-shell--hidden');
  shell.setAttribute('aria-hidden', 'false');
  shell.classList.add('boot-shell--error');
  const status = shell.querySelector<HTMLElement>('.boot-shell__status');
  if (status) status.textContent = message;
  const retry = shell.querySelector<HTMLButtonElement>('#boot-shell-retry');
  if (retry) {
    retry.hidden = false;
    // Property assignment (not addEventListener) so repeated failures never stack handlers.
    retry.onclick = onRetry;
  }
}
